/**
 * The content panel every zone shows: a Canvas whose Text children read the
 * zone's Role, Project or Degree through bindings (docs/planning/05-ui-authoring.md).
 * Level generation builds new panels with it, and the format-3 upgrade turns
 * old World UI panels into the same thing, so both look alike.
 *
 * Only bindings and the panel's own name appear here, never copy (rule 6).
 */
import type { ManifestEntity } from "./content";

export type PanelContent = "Role" | "Project" | "Degree";

/** A Text child's name, text and style, top to bottom. */
const PANEL_TEXTS: Readonly<Record<PanelContent, readonly (readonly [string, string, string])[]>> =
  {
    Role: [
      ["Title", "{Role.title}", "title"],
      ["Meta", "{Role.company} · {Role.start} - {Role.end}", "meta"],
      ["Scope", "{Role.scope}", "body"],
      ["Highlights", "{Role.highlights}", "body"],
    ],
    Project: [
      ["Title", "{Project.name}", "title"],
      ["Meta", "{Project.tagline}", "meta"],
      ["Summary", "{Project.summary}", "body"],
      ["Highlights", "{Project.highlights}", "body"],
    ],
    Degree: [
      ["Title", "{Degree.degree}", "title"],
      ["Meta", "{Degree.school} · {Degree.years}", "meta"],
      ["Concentration", "{Degree.concentration}", "body"],
      ["Highlights", "{Degree.highlights}", "body"],
    ],
  };

/**
 * The panel's Text children: its name as the eyebrow, then the content's
 * title, meta line and body. `idFor` turns a base id (`<panel id>-title`)
 * into one the level doesn't use yet.
 */
export function contentPanelTexts(
  panel: { readonly id: string; readonly name: string },
  content: PanelContent,
  idFor: (base: string) => string = (base) => base,
): ManifestEntity[] {
  const lines = [["Eyebrow", panel.name, "eyebrow"] as const, ...PANEL_TEXTS[content]];
  return lines.map(([name, text, style]) => ({
    id: idFor(`${panel.id}-${name.toLowerCase()}`),
    name,
    components: { Text: { text, style } },
  }));
}
